import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import useSWRInfinite, { SWRInfiniteKeyLoader } from "swr/infinite";
import useSWR from "swr";
import { SegmentedControl } from "@telegram-apps/telegram-ui";
import {
    fetchGroupRecordList,
    fetchInviteRecordList,
    fetchRebateRecordList,
} from "~/api/endpoints";
import FooterCardDesc from "../Head/FooterCardDesc";
import RebateList from "./RebateList";
import InviteList from "./InviteList";
import { hapticFeedback } from "@telegram-apps/sdk-react";
import GroupList from "./GroupList";

export const PAGE_SIZE = 20;

type RebateAndInviteListProps = {
    rate?: number;
};

const RebateAndInviteList: React.FC<RebateAndInviteListProps> = ({ rate }) => {
    const { t } = useTranslation();
    const getRebateListKey: SWRInfiniteKeyLoader = (
        index,
        previousPageData
    ) => {
        if (previousPageData && !previousPageData.length) return null;
        return [`RebatePageRecordList:${index + 1}`, index + 1];
    };
    const rebateListFetcher = (args: [string, number] | null) => {
        if (!args?.[1]) return null;
        return fetchRebateRecordList(Number(args[1]), PAGE_SIZE);
    };

    const {
        data: rebateList,
        size: rebateListPageNum,
        setSize: setRebateListPageNum,
    } = useSWRInfinite(getRebateListKey, rebateListFetcher, {
        fallbackData: [],
        keepPreviousData: true,
        revalidateFirstPage: false,
    });

    const [inviteListSortType, setInviteListSortType] = useState<0 | 1>(0);

    const getInviteListKey: SWRInfiniteKeyLoader = (
        index,
        previousPageData
    ) => {
        if (previousPageData && !previousPageData.length) return null;
        return [
            `RebatePageInviteList:${index + 1}`,
            index + 1,
            inviteListSortType,
        ];
    };

    const inviteListFetcher = (args: [string, number, number] | null) => {
        if (!args?.[1]) return null;
        return fetchInviteRecordList(args[1], PAGE_SIZE, args[2]);
    };

    const {
        data: inviteList,
        size: inviteListPageNum,
        setSize: setInviteListPageNum,
    } = useSWRInfinite(getInviteListKey, inviteListFetcher, {
        fallbackData: [],
        keepPreviousData: true,
        revalidateFirstPage: false,
    });

    const { data: groupList } = useSWR(
        "RebatePageGroupList",
        fetchGroupRecordList,
        {
            fallbackData: [],
            keepPreviousData: true,
        }
    );

    const hasRecord =
        (rebateList?.length ?? 0) > 0 ||
        (inviteList?.length ?? 0) > 0 ||
        (groupList?.length ?? 0) > 0;

    const navs = [t("reward"), t("friends"), t("group_record")];
    const [selectedNav, setSelectedNav] = React.useState(0);

    if (hasRecord) {
        return (
            <>
                <SegmentedControl className="bg-bg_bottom">
                    {navs.map((nav, index) => (
                        <SegmentedControl.Item
                            className={clsx("text-text_1 text-body4", {
                                "bg-bg_top": index === selectedNav,
                            })}
                            key={index}
                            onClick={() => {
                                hapticFeedback.impactOccurred("light");
                                setSelectedNav(index);
                            }}
                            selected={index === selectedNav}
                        >
                            <div
                                className={clsx(
                                    index === selectedNav ? "font-Semibold" : ""
                                )}
                            >
                                {nav}
                            </div>
                        </SegmentedControl.Item>
                    ))}
                </SegmentedControl>
                {selectedNav === 0 ? (
                    <RebateList
                        rebateList={rebateList}
                        rebateListPageNum={rebateListPageNum}
                        setRebateListPageNum={setRebateListPageNum}
                    />
                ) : selectedNav === 1 ? (
                    <InviteList
                        inviteList={inviteList}
                        inviteListPageNum={inviteListPageNum}
                        setInviteListPageNum={setInviteListPageNum}
                        setInviteListSortType={setInviteListSortType}
                    />
                ) : (
                    <GroupList groupList={groupList} />
                )}
            </>
        );
    }

    return <FooterCardDesc rate={rate} />;
};

export default RebateAndInviteList;
